import React from 'react'
import { observer } from 'mobx-react-lite'
import { Clock, Loader2 } from 'lucide-react'
import { useTranslation } from '@/hooks/useTranslation'
import { formatMinutes } from './MinutesOfferModal'
import { meteringStore } from './meteringStore'
import { formatDay, subscriptionStore } from './subscriptionStore'

/**
 * Bottom left of the Module2 sidebar, for a B2C subscriber: all their minutes as one
 * number (subscription plus top-ups, each counted once), the plan with its renewal or
 * end date, and a way to manage it. Pressing the number opens the top-up offer.
 */
const SidebarMinutes: React.FC = () => {
  const { t, currentLanguage } = useTranslation()
  const { mine, openingPortal } = subscriptionStore
  if (!mine?.applies || !mine.hasAccess) return null

  const subscription = mine.subscription
  const periodEnd = formatDay(subscription?.currentPeriodEnd, currentLanguage)
  const status = subscription?.status
  const lowOnMinutes = mine.totalSeconds < 60

  return (
    <div className="hidden md:block mt-auto pt-3 w-full shrink-0">
      <div className="rounded-xl border p-3" style={{ borderColor: 'var(--m2-line)', background: '#fff' }}>
        <button
          type="button"
          onClick={() => meteringStore.openOffer()}
          className="flex w-full items-center gap-2 rounded-md text-left hover:opacity-80"
          title={t('subscription.sidebar.buy')}
        >
          <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-md" style={{ background: 'var(--m2-mint)', color: 'var(--m2-brand)' }}>
            <Clock style={{ width: 14, height: 14 }} />
          </span>
          <span className="min-w-0">
            <span className="block text-[10px] font-semibold uppercase tracking-[1px]" style={{ color: 'var(--m2-muted)' }}>
              {t('subscription.sidebar.minutesLabel')}
            </span>
            <span className="block text-[15px] font-bold tabular-nums" style={{ color: lowOnMinutes ? '#B42318' : 'var(--m2-ink)' }}>
              {t('subscription.sidebar.minutes', { count: formatMinutes(mine.totalSeconds) })}
            </span>
          </span>
        </button>

        <div className="mt-2 border-t pt-2 text-[11px]" style={{ borderColor: 'var(--m2-line)', color: 'var(--m2-muted)' }}>
          {mine.plan && <div className="truncate font-medium" style={{ color: 'var(--m2-ink)' }}>{mine.plan.name}</div>}
          {status === 'past_due' ? (
            <div className="text-[#B42318]">{t('subscription.sidebar.pastDue')}</div>
          ) : periodEnd ? (
            <div>{subscription?.cancelAtPeriodEnd ? t('subscription.sidebar.ends', { date: periodEnd }) : t('subscription.sidebar.renews', { date: periodEnd })}</div>
          ) : null}
          <button
            type="button"
            disabled={openingPortal}
            onClick={() => subscriptionStore.openPortal()}
            className="mt-1 inline-flex items-center gap-1 font-semibold underline underline-offset-2 disabled:opacity-60"
            style={{ color: 'var(--m2-brand)' }}
          >
            {openingPortal && <Loader2 className="h-3 w-3 animate-spin" />}
            {t('subscription.sidebar.manage')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default observer(SidebarMinutes)
